/**
 * Webhook-health hook — #1438.
 *
 * Thin TanStack Query wrapper around `webhookHealthAPI.get(windowHours)`
 * (read-only per-topic delivery health). Mirrors `useWebhookFailures`:
 * the window is threaded into the query key so a window switch re-keys the
 * cache and refetches; a 30s focused-tab poll keeps the operator panel
 * fresh without hammering the endpoint in the background.
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import { webhookHealthAPI, type WebhookHealthSnapshot } from '@/services/webhook-health';

/** 15s — the panel is operational but not per-second actionable. */
const STALE_MS = 15_000;
/** 30s focused-tab refetch; quiet in the background. */
const BACKGROUND_REFETCH_MS = 30_000;

export function useWebhookHealth(windowHours: number, options?: { enabled?: boolean }) {
  return useQuery<WebhookHealthSnapshot, Error>({
    queryKey: queryKeys.webhookHealth.snapshot({ windowHours }),
    queryFn: () => webhookHealthAPI.get(windowHours),
    enabled: options?.enabled ?? true,
    staleTime: STALE_MS,
    refetchInterval: BACKGROUND_REFETCH_MS,
    refetchIntervalInBackground: false,
  });
}
